// Výpis kategorie a detail značky (issue #13, redesign `design_handoff_category/`).
//
// Enhancement nad fixním Shoptet DOM — filtrování i řazení zůstává 100% nativní
// (`data-url` na inputech, `.listSorting__control`), my jen přeskládáme a obalíme:
//   • dvousloupcový výpis: nativní `aside.sidebar` (v něm `#filters`) vlevo od
//     PRVNÍHO řádku, vpravo drobečky → hlavička → Top produkty → toolbar →
//     `#products` → paginace → SEO. Proto se do `.bs-listing__main` stěhují
//     i drobečky — filtr pak začíná v jedné linii s nimi (README §Rozvržení).
//   • `#filters` dostane hlavičku „Filtrace", chipy aktivních filtrů (klik =
//     nativní odškrtnutí), rozpad boolean sekce na „Dostupnost" / „Štítky"
//     a u sekcí nad 5 položek přepínač „Zobrazit více (N)"
//   • počet produktů vedle H1; první odstavec popisu je perex, zbytek SEO blok dole
//   • hlavička kategorie s fotografií vpravo (280 px) — zdrojem je admin blok
//     `#bs-cat-photos`; bez záznamu žádná fotka, hlavička přes celou šířku
//   • dlaždice podkategorií jako karty 3 ve sloupci (kolečko + název + chevron)
//   • nativní blok „Nejprodávanější" → „Top produkty v kategorii" (#1 + dva řádky)
//   • poradna panel (§7) a promo karta ve mřížce (§15)
//
// Sidebar strom kategorií a „Top 10" box design na výpisu nemá — schová je CSS
// až pod `html.bs-listing-ready`, takže bez JS zůstane funkční nativní sidebar.
//
// Detail značky (`type-manufacturer-detail`) sdílí markup, ale nemá `.category-top`
// ani `.category-perex` — popis jsou přímé děti `.manufacturerDetail`.
//
// Výpis z vlastního indexu (procházení výprodeje, saleBrowse.js) se registruje
// v listingSource.js: počty, chipy a „Vymazat vše" se pak ptají jeho, pás kampaně,
// promo karty a prázdné stavy nativního výpisu se nekreslí.
//
// Idempotentní, re-init po AJAX překreslení (ShoptetDOMPageContentLoaded).

import { icon } from './icon.js';
import { discountPercent } from './productPrice.js';
import { CONTACT, phoneAction, phonesOn, telHref } from './headerTools.js';
import { MEDIA } from './instanceUrl.js';
import { page, withPages, catalogRoot } from './pages.js';
import { asChatTrigger, chatActions } from './supportChat.js';
import { activeCampaign, trackPromo } from './campaign.js';
import { listingSource } from './listingSource.js';
import L from '../locale/cs/categoryPage.js';
import LSB from '../locale/cs/saleBrowse.js';

const READY = 'bs-listing-ready';

const LABEL_FILTER = L.filterTitle;
const LABEL_CLEAR = L.clearAll;
const LABEL_AVAILABILITY = L.availability;
const LABEL_TAGS = L.tags;
const LABEL_SEO_TITLE = L.seoTitle;
const LABEL_SEO_ANCHOR = L.seoAnchor;
const SEO_ID = 'bs-cat-seo';

// Blok „Top produkty v kategorii X" staví na nativním „Nejprodávanější"
// (`.products-top-wrapper`) — data jsou tedy Shoptetu, ne naše.
const LABEL_TOP_FLAG = L.topFlag;

// Sekce filtru nad tenhle počet položek se sbalí pod „Zobrazit více (N)"
const SEC_LIMIT = 5;
const LABEL_SEC_LESS = L.secLess;

// Perex má na mobilu clamp 3 řádky (m04 §3) — přepínač dodáváme my
const LABEL_PEREX_MORE = L.perexMore;
const LABEL_PEREX_LESS = L.perexLess;

// Prázdné stavy (m04 §„Stavy"): filtr bez výsledku vs. kategorie bez produktů.
// Nadřazené kategorie Shoptetu neagregují produkty z podkategorií (CLAUDE.md),
// takže druhý stav je na Jiposu běžný, ne okrajový.
const LABEL_NORESULT_TITLE = L.noResultTitle;
const LABEL_NORESULT_TEXT = L.noResultText;
const LABEL_NORESULT_CTA = L.noResultCta;
const LABEL_EMPTY_TITLE = L.emptyTitle;
const LABEL_EMPTY_TEXT_SUB = L.emptyTextSub;
const LABEL_EMPTY_TEXT = L.emptyText;

// Poradna panel (§7). Kontakt sdílí `CONTACT` s hlavičkou i patičkou; cena dopravy
// se vědomě neuvádí — handoff ji má jen jako placeholder (otevřený bod A4, zdroj
// pravdy je admin), stejně jako trust řádek patičky.
const ADVICE = {
  eyebrow: L.advice.eyebrow,
  title: L.advice.title,
  text: L.advice.text,
  textNoPhone: L.advice.textNoPhone,
  actions: withPages([
    // se zapnutým chatem ho poradna nabízí místo odkazu na kontakt (chatActions)
    { chat: 'category', primary: true },
    {
      href: telHref(CONTACT.phone),
      label: L.advice.callLabel,
      icon: 'headset',
      primary: true,
      alt: { href: page('contact'), chatless: true }, // bez telefonu vede poradna na kontakt
    },
    { href: page('store'), label: L.advice.storeLabel, icon: 'store' },
  ]),
  perks: [
    { icon: 'truck', text: L.advice.perkShipping },
    { icon: 'refresh', text: L.advice.perkReturn },
    { icon: 'shield', text: L.advice.perkService },
  ],
};

// Promo karta ve mřížce (§15). Míří na Slevoakce — jedinou akční nákupní
// stránku shopu (zadání 2026-08-31, nahrazuje dřívější kartu Jipos Tools);
// per kategorii ji může přebít admin blok `#bs-listing-promos`
// (`<a data-bs-promo="a|b" data-bs-after="4" data-bs-title="…"
//  data-bs-img="…" data-bs-img-mobile="…" …>`). Fotky jsou generované podklady
// bez textu (text kreslí karta) ve dvou ořezech: desktop = portrétní buňka
// mřížky, mobil = širokoúhlá dlaždice přes 2 sloupce.
const DEFAULT_PROMOS = [
  {
    variant: 'a',
    after: 4,
    eyebrow: L.promo.eyebrow,
    title: L.promo.title,
    body: L.promo.body,
    cta: L.promo.cta,
    href: page('sale'),
    img: MEDIA + 'promo-slevoakce-desktop.jpg',
    imgMobile: MEDIA + 'promo-slevoakce-mobile.jpg',
  },
];

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

// ---------------------------------------------------------------------------
// Scroll na začátek produktů po změně filtrace (zadání 2026-08-31)
// ---------------------------------------------------------------------------
// Nativní AJAX překreslení po filtru vrací scroll na vrchol stránky (sonda:
// scrollY 1200 → 29) — uživatel pak musí hlavičkou kategorie projít znovu, aby
// viděl výsledek. Interakce s filtrem (change kdekoli v `#filters` — checkbox,
// cenová pole; chytá i chipy, ty klikají nativní input) vyzbrojí flag a re-init
// po `ShoptetDOMPageContentLoaded` sroluje toolbar pod sticky header. Delegace
// na dokumentu, protože AJAX celý `#filters` vyměňuje. Flag má TTL — ten samý
// event střílí i přidání do košíku a stará interakce nesmí scrollnout cizí
// překreslení. S otevřeným mobilním sheetem (body `overflow: hidden`) se scroll
// odkládá — mobileFilter ho flushne při zavření (`flushListingScroll`).
const SCROLL_TTL = 15000;
const SCROLL_GAP = 12;
let scrollWanted = 0;
let scrollWired = false;

function scrollToProducts() {
  const toolbar = document.querySelector('.bs-toolbar');
  // mobil toolbar nemá (skrytý) → cílem je mřížka; offsetParent odfiltruje display:none
  const target = toolbar && toolbar.offsetParent ? toolbar : document.querySelector('#products');
  if (!target) return;

  let offset = SCROLL_GAP;
  const header = document.getElementById('header');
  if (header) offset += Math.round(header.getBoundingClientRect().height);
  // mobilní sticky lišta filtru sedí POD headerem — produkty nesmí zajet pod ni
  const mbar = document.querySelector('.bs-mbar');
  if (mbar && mbar.offsetParent && target !== toolbar) {
    offset += Math.round(mbar.getBoundingClientRect().height);
  }

  const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
}

export function flushListingScroll() {
  const armed = scrollWanted;
  scrollWanted = 0;
  if (!armed || Date.now() - armed > SCROLL_TTL) return;
  // po vlastním scrollu Shoptetu (reset na vrchol) — až se layout usadí
  requestAnimationFrame(scrollToProducts);
}

function maybeScrollToProducts() {
  if (!scrollWanted) return;
  if (document.body.classList.contains('bs-fsheet-open')) return; // flush až při zavření sheetu
  flushListingScroll();
}

function wireListingScroll() {
  if (scrollWired) return;
  scrollWired = true;
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest && e.target.closest('#filters')) scrollWanted = Date.now();
  });
}

function isListing() {
  const b = document.body.classList;
  return b.contains('type-category') || b.contains('type-manufacturer-detail');
}

// 1 produkt / 2–4 produkty / 5+ produktů
export function productsWord(n) {
  return L.productsWord(n);
}

// Čísla z nativní hlavičky „Stránka X z Y - Z položek celkem".
export function listingStats() {
  const source = listingSource();
  if (source) return source.stats();
  const pag = document.querySelector('.category-header-pagination');
  const nums = pag
    ? Array.prototype.map
        .call(pag.querySelectorAll('strong'), function (s) {
          return parseInt((s.textContent || '').replace(/\D/g, ''), 10);
        })
        .filter(function (n) {
          return isFinite(n);
        })
    : [];

  let total = nums.length >= 3 ? nums[2] : NaN;
  if (!isFinite(total)) {
    const fallback = document.querySelector('.filter-total-count strong');
    total = fallback ? parseInt((fallback.textContent || '').replace(/\D/g, ''), 10) : NaN;
  }

  return {
    page: nums.length >= 3 ? nums[0] : 1,
    pages: nums.length >= 3 ? nums[1] : 1,
    total: total,
    rendered: document.querySelectorAll('#products > .product').length,
  };
}

// ---------------------------------------------------------------------------
// Rozvržení: filtr vlevo, produkty vpravo (design §6)
// ---------------------------------------------------------------------------
// Obal, do kterého se dvousloupcová mřížka vkládá: `main#content` (kategorie
// i detail značky). `#category-header` je uvnitř `.category-content-wrapper`,
// takže jeho rodič by ustřihl hlavičku kategorie i blok „Nejprodávanější".
function listingHost() {
  const top = document.querySelector('.category-top, .manufacturerDetail');
  if (top && top.parentElement) return top.parentElement;
  const header = document.querySelector('#category-header');
  const wrap = header && header.closest('.category-content-wrapper');
  if (wrap && wrap.parentElement) return wrap.parentElement;
  const products = document.querySelector('#products');
  return header ? header.parentElement : products && products.parentElement;
}

function buildLayout() {
  const host = listingHost();
  // Po prvním průchodu je `.category-top` už UVNITŘ `.bs-listing__main`, takže
  // `listingHost()` vrací právě ten sloupec — hotový obal je jeho PŘEDEK, ne
  // potomek. Re-init (Shoptet střílí `ShoptetDOMPageContentLoaded` i po přidání
  // do košíku / načtení mini-cartu) by jinak obalil sloupec podruhé a mřížka
  // by se zhroutila na 268 px (nález 2026-08-24: „kategorie se rozpadla po
  // hoveru na košík"). Nový obal patří jen tam, kde žádný nestojí.
  if (!host || host.querySelector('.bs-listing') || host.closest('.bs-listing')) return;

  // Zdroj levého sloupce = kde Shoptet zrovna nechal `#filters` (U3 #110):
  //   • kategorie (první render): `aside.sidebar`
  //   • detail značky: `#filters-wrapper` v toku před `#category-header`
  //   • AJAX fragment po filtrování: volný `#filters` v toku obsahu
  //     (aside zmizí a `#filters-wrapper` zůstane prázdný)
  // Bere se nejbližší obal, ať default JS (stěhování filtru na úzkých šířkách)
  // pořád trefuje tentýž element. Mobilní UA render sloupec NEstaví — filtr si
  // bere off-canvas (mobileFilter.js) a prázdný sloupec by v mřížce nechal gap.
  const filters = document.querySelector('#filters');
  let col = null;
  if (filters && !document.body.classList.contains('mobile')) {
    col =
      filters.closest('aside.sidebar') ||
      filters.closest('#filters-wrapper') ||
      filters.closest('.filters-wrapper') ||
      filters;
  }

  // celý obsah `main#content` jde doprava (posbírat PŘED vložením obalu);
  // zdroj sloupce mezi sourozenci přeskočit — půjde doleva
  const rest = [];
  let node = host.firstElementChild;
  while (node) {
    if (node !== col) rest.push(node);
    node = node.nextElementSibling;
  }
  if (!rest.length) return;

  const listing = el('div', 'bs-listing');
  const main = el('div', 'bs-listing__main');

  if (col) {
    col.classList.add('bs-filter-col');
    listing.appendChild(col);
  } else {
    listing.classList.add('bs-listing--wide');
  }
  listing.appendChild(main);

  // Drobečky se stěhují z `#content-wrapper` dovnitř pravého sloupce — návrh
  // s nimi lícuje horní hranu filtru. Jsou samostatná komponenta (_breadcrumbs),
  // na umístění nezávisí; bez nich (detail značky) se prostě nepřesouvá nic.
  const crumbs = document.querySelector('#content-wrapper > .breadcrumbs');
  if (crumbs) main.appendChild(crumbs);

  rest.forEach(function (n) {
    main.appendChild(n);
  });

  host.appendChild(listing);
}

// ---------------------------------------------------------------------------
// Filtr (design §6 „Levý filtr")
// ---------------------------------------------------------------------------
function filterInputs() {
  return Array.prototype.slice.call(document.querySelectorAll('#filters input[type="checkbox"]'));
}

function activeInputs() {
  return filterInputs().filter(function (i) {
    return i.checked;
  });
}

// Text labelu bez počtu v závorce (`.filter-count` je vlastní span)
function inputLabel(input) {
  const row = input.closest('div');
  const label = row && row.querySelector('label');
  if (!label) return input.value || '';
  const first = label.childNodes[0];
  const raw = first && first.nodeType === 3 ? first.textContent : label.textContent;
  return (raw || '').replace(/[\u00a0\s]+/g, ' ').trim();
}

// Aktivní filtry pro chipy — sdílí je i mobilní lišta (mobileFilter.js),
// ať se popisky i mechanika odebrání nerozejdou.
export function activeFilters() {
  const source = listingSource();
  if (source) return source.activeFilters();
  return activeInputs().map(function (input) {
    return {
      label: inputLabel(input),
      // odebrání = nativní odškrtnutí → default JS naviguje na URL bez filtru
      remove: function () {
        input.click();
      },
    };
  });
}

// Reset = nativní URL bez filtrů. Značka žije v cestě (`/topidla:boxer/`),
// sklad a štítky v query — proto obojí.
export function clearFilters() {
  const source = listingSource();
  if (source) source.clearFilters();
  else clearAll();
}

function clearAll() {
  if (!activeInputs().length) return;
  let url;
  try {
    url = new URL(window.location.href);
  } catch (e) {
    return;
  }
  ['stock', 'dd', 'priceMin', 'priceMax'].forEach(function (k) {
    url.searchParams.delete(k);
  });
  url.pathname = url.pathname.replace(/:[^/]*/, '');
  window.location.href = url.toString();
}

export function refreshChips() {
  const box = document.querySelector('.bs-filter__chips');
  if (!box) return;
  const clear = box.querySelector('.bs-filter__clear');
  box.textContent = '';

  const items = activeFilters();
  items.forEach(function (item) {
    const chip = el('button', 'bs-chip bs-chip--filter', item.label);
    chip.type = 'button';
    chip.appendChild(icon('close'));
    chip.addEventListener('click', item.remove);
    box.appendChild(chip);
  });

  box.classList.toggle('is-empty', items.length === 0);
  if (clear) {
    box.appendChild(clear); // „Vymazat vše" stojí za posledním chipem
    clear.classList.toggle('is-active', items.length > 0);
    clear.disabled = items.length === 0;
  }
}

function enhanceFilter() {
  const filters = document.querySelector('#filters');
  if (!filters || filters.dataset.bsFilter) return;
  filters.dataset.bsFilter = '1';

  // hlavička + chipy aktivních filtrů („Vymazat vše" patří k chipům)
  const head = el('div', 'bs-filter__head');
  const title = el('div', 'bs-filter__title');
  title.appendChild(icon('grid'));
  title.appendChild(el('span', null, LABEL_FILTER));
  head.appendChild(title);

  const chips = el('div', 'bs-filter__chips is-empty');
  const clear = el('button', 'bs-filter__clear', LABEL_CLEAR);
  clear.type = 'button';
  clear.addEventListener('click', clearFilters);
  chips.appendChild(clear);

  filters.insertAdjacentElement('afterbegin', chips);
  filters.insertAdjacentElement('afterbegin', head);

  // cena: pole pod slider + oddělovač mezi nimi (design)
  const slider = filters.querySelector('.slider-wrapper');
  if (slider) {
    slider.classList.add('bs-filter__sec');
    const range = slider.querySelector('.slider-header');
    const content = slider.querySelector('.slider-content');
    if (range && content) {
      content.insertAdjacentElement('afterend', range);
      const to = range.querySelector('.to');
      if (to && !range.querySelector('.bs-filter__dash')) {
        to.insertAdjacentElement('beforebegin', el('span', 'bs-filter__dash', '–'));
      }
    }
  }

  // nativní boolean sekce míchá „Na skladě" a štítky do jednoho bloku bez titulků
  const bool = filters.querySelector('.filter-section-boolean .param-filter-top') || filters.querySelector('.filter-section-boolean');
  if (bool) {
    Array.prototype.slice.call(bool.children).forEach(function (form) {
      if (form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      let label = null;
      if (/setStockFilter/i.test(action)) label = LABEL_AVAILABILITY;
      else if (/setDoubledotFilter/i.test(action)) label = LABEL_TAGS;
      if (!label) return;
      const sec = el('div', 'bs-filter__sec');
      form.insertAdjacentElement('beforebegin', sec);
      sec.appendChild(el('div', 'bs-filter__sec-title', label));
      sec.appendChild(form);
    });
  }

  const manufacturer = filters.querySelector('#manufacturer-filter');
  if (manufacturer) manufacturer.classList.add('bs-filter__sec');

  // parametrické sekce (filtrační parametry produktů) — v době #13/#14 je dev katalog
  // neměl, takže sem nedosáhly a kreslily se bez odsazení i oddělovače
  Array.prototype.forEach.call(
    filters.querySelectorAll('.filter-section-parametric'),
    function (sec) { sec.classList.add('bs-filter__sec'); }
  );

  Array.prototype.forEach.call(filters.querySelectorAll('.bs-filter__sec'), collapseSection);

  filters.addEventListener('change', refreshChips);
  refreshChips();
}

// Sekce nad `SEC_LIMIT` položek ukazuje prvních pět a zbytek schová pod
// „Zobrazit více (N)". Řádky jsou přímé `div` fieldsetu (vedle nich tam žije
// jen `input[type=hidden][name=referer]`). Sekce s vybranou položkou mimo
// prvních pět se otevře rovnou — jinak by uživatel svůj filtr neviděl.
function collapseSection(sec) {
  if (sec.dataset.bsSecMore) return;
  const fieldset = sec.querySelector('fieldset');
  if (!fieldset) return;

  const rows = Array.prototype.slice.call(fieldset.children).filter(function (n) {
    return n.tagName === 'DIV' && n.querySelector('input[type="checkbox"]');
  });
  if (rows.length <= SEC_LIMIT) return;
  sec.dataset.bsSecMore = '1';

  const hidden = rows.slice(SEC_LIMIT);
  const btn = el('button', 'bs-filter__more');
  btn.type = 'button';
  const label = el('span', null, L.secMore(hidden.length));
  btn.appendChild(label);
  btn.appendChild(icon('chevron-down'));

  const apply = function (open) {
    hidden.forEach(function (row) {
      row.classList.toggle('bs-filter__row--hidden', !open);
    });
    btn.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', String(open));
    label.textContent = open ? LABEL_SEC_LESS : L.secMore(hidden.length);
  };

  const preselected = hidden.some(function (row) {
    const input = row.querySelector('input[type="checkbox"]');
    return input && input.checked;
  });
  apply(preselected);

  btn.addEventListener('click', function () {
    apply(!btn.classList.contains('is-open'));
  });
  fieldset.insertAdjacentElement('afterend', btn);
}

// ---------------------------------------------------------------------------
// Hlavička: H1 + počet produktů (design §2)
// ---------------------------------------------------------------------------
function heading() {
  return document.querySelector('h1.category-title') || document.querySelector('#content h1');
}

function enhanceHeading() {
  const h1 = heading();
  if (!h1 || h1.closest('.bs-cat-head')) return;

  const head = el('div', 'bs-cat-head');
  h1.insertAdjacentElement('beforebegin', head);
  head.appendChild(h1);

  const stats = listingStats();
  if (!isFinite(stats.total)) return;
  head.appendChild(el('span', 'bs-cat-count', stats.total + ' ' + productsWord(stats.total)));
}

// ---------------------------------------------------------------------------
// Hlavička s fotografií (README §Hlavička kategorie)
// ---------------------------------------------------------------------------
// Lifestyle fotku nativní DOM nenese a `og:image` kategorie je produktový výřez
// na bílé (cover by ho ustřihl). Zdrojem je proto admin blok v HTML kódech:
//   <div id="bs-cat-photos" hidden>
//     <span data-bs-cat="aku-retezove-pily" data-bs-img="/user/…/pily.jpg"></span>
//   </div>
// Kategorie bez záznamu spadne na variantu `showFoto=false` z návrhu — hlavička
// přes celou šířku a větší odsazení. Nic se nedomýšlí.
function categorySlug() {
  const first = (window.location.pathname || '').split('/').filter(Boolean)[0] || '';
  return first.split(':')[0].toLowerCase(); // značka žije v cestě (`/topidla:boxer/`)
}

function categoryPhoto() {
  const host = document.querySelector('#bs-cat-photos');
  const slug = categorySlug();
  const def = host && slug
    ? host.querySelector('[data-bs-cat="' + slug.replace(/["\\]/g, '') + '"][data-bs-img]') ||
      host.querySelector('[data-bs-cat=""][data-bs-img]') // fallback pro všechny
    : null;
  const src = def && def.getAttribute('data-bs-img');
  if (src) {
    const img = document.createElement('img');
    img.className = 'bs-cat-intro__photo';
    img.src = src;
    img.alt = def.getAttribute('data-bs-alt') || '';
    img.loading = 'lazy';
    return img;
  }
  // Bez lifestyle fotky žádná fotka (rozhodnutí Tomáše 2026-08-26): nativní
  // `og:image` kategorie je produktový výřez a v hlavičce byl „k ničemu", jen
  // dělal prázdné místo — hlavička jde přes celou šířku (varianta showFoto=false).
  return null;
}

// `.category-top` → grid „text | foto". Vnitřek se sype do `__body`, aby text
// držel vlastní sloupec i bez fotky (a `max-width` perexu měl k čemu se vztáhnout).
function buildIntro() {
  const h1 = heading();
  const top = h1 && h1.closest('.category-top, .manufacturerDetail');
  if (!top || top.classList.contains('bs-cat-intro')) return;
  top.classList.add('bs-cat-intro');

  // Blok „Nejprodávanější" Shoptet renderuje podle stránky buď jako sourozence
  // `.category-top`, nebo DOVNITŘ ní (živý výpis kategorie). V druhém případě by
  // skončil v textovém sloupci hlavičky vedle fotky — vytáhneme ho ven, ať je
  // z něj samostatný blok pod hlavičkou jako v návrhu.
  const best = top.querySelector(':scope > .products-top-wrapper');
  if (best) top.insertAdjacentElement('afterend', best);

  const body = el('div', 'bs-cat-intro__body');
  Array.prototype.slice.call(top.children).forEach(function (n) {
    body.appendChild(n);
  });
  top.appendChild(body);

  // Podkategorie zůstávají SOUROZENCEM textu, ne jeho dítětem: na desktopu je
  // grid areas posadí pod perex do levého sloupce, na mobilu (jeden sloupec)
  // musí přijít až ZA fotku — návrh má pořadí perex → foto → pás dlaždic.
  const subs = body.querySelector(':scope > ul.subcategories');

  const photo = categoryPhoto();
  if (photo) {
    top.appendChild(photo);
    top.classList.add('bs-cat-intro--photo');
  }
  if (subs) top.appendChild(subs);
}

// ---------------------------------------------------------------------------
// SEO text pod výpisem + poradna panel (design §7)
// ---------------------------------------------------------------------------
// Přepínač perexu (m04 §3). Desktop ukazuje popis celý (CSS ho schová), na mobilu
// by tři a víc řádků odsunuly produkty pod fold → clamp 3 + „Zobrazit celý popis".
function buildPerexToggle(perex) {
  const next = perex.nextElementSibling;
  if (next && next.classList.contains('bs-cat-perex__toggle')) return;

  const btn = el('button', 'bs-cat-perex__toggle');
  btn.type = 'button';
  btn.setAttribute('aria-expanded', 'false');
  const label = el('span', null, LABEL_PEREX_MORE);
  btn.appendChild(label);
  btn.appendChild(icon('chevron-down'));

  btn.addEventListener('click', function () {
    const open = perex.classList.toggle('is-open');
    btn.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', String(open));
    label.textContent = open ? LABEL_PEREX_LESS : LABEL_PEREX_MORE;
  });

  perex.insertAdjacentElement('afterend', btn);
}

function buildSeo() {
  const h1 = heading();
  if (!h1) return null;
  const intro = h1.closest('.category-top, .manufacturerDetail') || h1.parentElement;
  if (!intro) return null;
  // Popis kategorie je v `.category-perex`, na detailu značky rovnou pod H1. Kategorie bez
  // popisu `.category-perex` nemá: do 2026-10-13 se perexem stal pás Top produktů
  // a mobil pod hlavičkou ukazoval „Zobrazit celý popis" bez popisu (/zahrada/).
  const perex = intro.querySelector('.category-perex') || (intro.classList.contains('category-top') ? null : intro);
  if (!perex) return null;

  const blocks = Array.prototype.slice.call(perex.children).filter(function (n) {
    return !n.classList.contains('bs-cat-head') && !n.classList.contains('subcategories') &&
      !n.classList.contains('products-top-wrapper') && n.tagName !== 'H1';
  });
  if (!blocks.length) return null;

  blocks[0].classList.add('bs-cat-perex');
  buildPerexToggle(blocks[0]);
  if (blocks.length < 2) return null;

  const sec = el('div', 'bs-cat-seo');
  sec.id = SEO_ID;
  // Popis ve formátu v2 (docs/ops/format-popisu-kategorie.md) nese vlastní H2
  // s frází kategorie hned za perexem — generický titulek dostávají jen starší
  // texty bez nadpisu; na detailu značky je z toho blok „O značce X" (#32).
  const ownHeading = blocks[1].tagName === 'H2';
  if (!ownHeading) {
    const brand = document.body.classList.contains('type-manufacturer-detail')
      ? (h1.textContent || '').replace(/\s+/g, ' ').trim()
      : '';
    sec.appendChild(el('h2', null, brand ? L.seoBrand(brand) : LABEL_SEO_TITLE));
  }
  blocks.slice(1).forEach(function (b) {
    sec.appendChild(b);
  });

  const more = el('a', 'bs-cat-perex__more', LABEL_SEO_ANCHOR);
  more.href = '#' + SEO_ID;
  blocks[0].appendChild(document.createTextNode(' '));
  blocks[0].appendChild(more);

  return sec;
}

// Panel poradny. Sdílená komponenta — kromě výpisu ho staví i prázdný stav
// vyhledávání (#31) a přehled značek (#32), proto je skoro všechno v `def`
// volitelné: `icon` (kolečko nad titulkem), `eyebrow`, perky i ikony akcí.
// Světlou variantu z přehledu značek dělá modifikátor `.bs-advice--light`.
export function buildAdvice(def) {
  const spec = def || ADVICE;
  const panel = el('aside', 'bs-advice');
  if (spec.icon) {
    const mark = el('span', 'bs-advice__mark');
    mark.appendChild(icon(spec.icon));
    panel.appendChild(mark);
  }
  if (spec.eyebrow) panel.appendChild(el('div', 'bs-advice__eyebrow', spec.eyebrow));
  panel.appendChild(el('div', 'bs-advice__title', spec.title));
  panel.appendChild(el('p', 'bs-advice__text', !phonesOn() && spec.textNoPhone ? spec.textNoPhone : spec.text));

  // Kanály: chat → e-mail → telefon. Bez telefonu `tel:` akce vypadne nebo se
  // nahradí `alt` (phoneAction v headerTools.js), akce `chat` vznikne jen se
  // zapnutým chatem a `chatless` jen bez něj (chatActions v supportChat.js).
  // Primární zůstane jen první primární akce; žádná-li, přebírá ji první.
  let primed = false;
  const list = chatActions(spec.actions.map(phoneAction).filter(Boolean)).map(function (a) {
    if (!a.primary) return a;
    if (primed) return Object.assign({}, a, { primary: false });
    primed = true;
    return a;
  });
  if (list.length && !primed) list[0] = Object.assign({}, list[0], { primary: true });
  const actions = el('div', 'bs-advice__actions');
  list.forEach(function (a) {
    const link = el('a', 'bs-advice__cta' + (a.primary ? ' bs-advice__cta--primary' : ''));
    link.href = a.href;
    if (a.icon) link.appendChild(icon(a.icon));
    link.appendChild(el('span', null, a.label));
    if (a.chat) asChatTrigger(link, a.chat);
    actions.appendChild(link);
  });
  panel.appendChild(actions);

  if (spec.perks && spec.perks.length) {
    const perks = el('div', 'bs-advice__perks');
    spec.perks.forEach(function (p) {
      const row = el('div', 'bs-advice__perk');
      row.appendChild(icon(p.icon));
      row.appendChild(el('span', null, p.text));
      perks.appendChild(row);
    });
    panel.appendChild(perks);
  }

  // Volitelná patička pod čárou — jednořádková poznámka místo seznamu perků
  // (404 panel má „Zákaznická podpora Po–Čt 8:00–15:00, Pá 7:30–14:30", #33).
  if (spec.note) panel.appendChild(el('div', 'bs-advice__note', spec.note));

  return panel;
}

function buildBottom() {
  const header = document.querySelector('#category-header');
  // po `buildLayout()` je cílem konec pravého sloupce, jinak obal hlavičky
  const host = document.querySelector('.bs-listing__main') || (header ? header.parentElement : null);
  if (!host || host.querySelector('.bs-cat-bottom')) return;

  const bottom = el('div', 'bs-cat-bottom');
  const seo = buildSeo();
  if (seo) bottom.appendChild(seo);
  else bottom.classList.add('bs-cat-bottom--no-text');
  bottom.appendChild(buildAdvice());
  host.appendChild(bottom);
}

// ---------------------------------------------------------------------------
// Prázdné stavy (m04 „Stavy") — filtr bez výsledku vs. kategorie bez produktů
// ---------------------------------------------------------------------------
function buildEmptyPanel(variant, iconName, title, text) {
  const panel = el('div', 'bs-empty bs-empty--' + variant);
  const mark = el('span', 'bs-empty__icon');
  mark.appendChild(icon(iconName));
  panel.appendChild(mark);
  panel.appendChild(el('div', 'bs-empty__title', title));
  panel.appendChild(el('p', 'bs-empty__text', text));
  return panel;
}

// Nadřazená kategorie = poslední odkaz v drobečkové navigaci (domeček je bez textu)
function parentCrumb() {
  const links = Array.prototype.slice.call(document.querySelectorAll('.breadcrumbs a'));
  for (let i = links.length - 1; i >= 0; i -= 1) {
    const label = (links[i].textContent || '').replace(/\s+/g, ' ').trim();
    if (label) return { href: links[i].getAttribute('href'), label: label };
  }
  return null;
}

function buildEmptyStates() {
  if (listingSource()) return; // prázdný výběr kreslí zdroj výpisu sám
  const products = document.querySelector('#products');
  if (!products) return;

  const old = document.querySelector('.bs-empty');
  if (old) old.remove();
  document.body.classList.remove('bs-cat-empty');

  if (products.querySelectorAll(':scope > .product').length) return;

  // (a) filtr nic nenašel — výpis i filtr zůstávají, nabídneme reset
  if (activeInputs().length) {
    const panel = buildEmptyPanel('filter', 'search', LABEL_NORESULT_TITLE, LABEL_NORESULT_TEXT);
    const btn = el('button', 'bs-empty__cta', LABEL_NORESULT_CTA);
    btn.type = 'button';
    btn.addEventListener('click', clearAll);
    panel.appendChild(btn);
    products.insertAdjacentElement('afterend', panel);
    return;
  }

  // (b) prázdná kategorie — toolbar, mřížku i paginaci schová CSS
  document.body.classList.add('bs-cat-empty');
  const subs = document.querySelector('ul.subcategories li');
  const panel = buildEmptyPanel('cat', 'package', LABEL_EMPTY_TITLE, subs ? LABEL_EMPTY_TEXT_SUB : LABEL_EMPTY_TEXT);
  const parent = parentCrumb();
  if (parent && parent.href) {
    const link = el('a', 'bs-empty__cta');
    link.href = parent.href;
    link.appendChild(el('span', null, parent.label));
    link.appendChild(icon('arrow-right'));
    panel.appendChild(link);
  }
  products.insertAdjacentElement('afterend', panel);
}

// ---------------------------------------------------------------------------
// Promo karty ve mřížce (design §15)
// ---------------------------------------------------------------------------
function promoDefs() {
  const host = document.querySelector('#bs-listing-promos');
  if (host) {
    return Array.prototype.map.call(host.querySelectorAll('[data-bs-promo]'), function (n) {
      return {
        variant: n.getAttribute('data-bs-promo') || 'b',
        after: parseInt(n.getAttribute('data-bs-after'), 10) || 4,
        eyebrow: n.getAttribute('data-bs-eyebrow') || '',
        title: n.getAttribute('data-bs-title') || '',
        body: n.getAttribute('data-bs-body') || '',
        cta: n.getAttribute('data-bs-cta') || '',
        href: n.getAttribute('href') || '#',
        img: n.getAttribute('data-bs-img') || '',
        imgMobile: n.getAttribute('data-bs-img-mobile') || '',
      };
    });
  }
  // akční karta dává smysl jen na kategorii, ne na cizím detailu značky
  if (!document.body.classList.contains('type-category')) return [];
  // Po dobu kampaně: na akční nabídce a výprodeji žádná karta (vedla by na
  // stránku, kde zákazník stojí), jinde dlaždice výprodeje skladu, shop bez
  // výprodeje nechá kartu akční nabídky s názvem kampaně. Fotka kampaně
  // (`photo`) z obou udělá kartu s fotkou, texty zůstávají.
  const campaign = activeCampaign();
  if (campaign) {
    if (campaignPage(campaign)) return [];
    const photo = campaignPhoto(campaign);
    const tone = campaign.tone === 'ink' ? { tone: 'ink' } : {};
    const first =
      campaign.tile && campaign.clearance
        ? Object.assign(clearanceTileDef(campaign), photo, tone, { campaign: campaign, target: 'clearance' })
        : Object.assign({}, DEFAULT_PROMOS[0], { eyebrow: campaign.name, href: campaign.href }, photo, tone, { campaign: campaign, target: 'sale' });
    // vlna 2 Black Friday: druhá karta (5. řada) s Vánocemi ve výprodeji
    return campaign.xmas ? [first, Object.assign(xmasTileDef(campaign), { campaign: campaign, target: 'xmas' })] : [first];
  }
  return DEFAULT_PROMOS;
}

// Dlaždice Vánoc ve výprodeji (manifest `campaigns[].xmas`, vlna 2 Black Friday):
// packshot vánočního zboží z výprodeje se zásobou, cíl = akční nabídka zúžená na
// kořen Vánoc (stav procházení výprodeje v adrese, saleBrowse.js).
function xmasTileDef(campaign) {
  const root = catalogRoot('xmas');
  const slug = root ? root.replace(/^\/|\/$/g, '') : '';
  return {
    variant: 'product',
    after: 4,
    eyebrow: campaign.name,
    title: campaign.t.xmasTitle(campaign.xmas.claimPct),
    body: campaign.t.xmasBody,
    cta: campaign.t.xmasCta,
    href: campaign.href + (slug ? '?' + LSB.params.cat + '=' + slug : ''),
    img: campaign.xmas.tile.img,
  };
}

// Fotka karty mezi produkty po dobu kampaně (manifest `campaigns[].photo`,
// vizuál E 2026-10-07): dlaždice pak není packshot na žlutém poli, ale fotka
// s červeným přesvitem (varianta a) ve dvou ořezech. Pás na stránce akční
// nabídky (`buildCampaignBand`) drží packshot, jeho široké rozvržení je pro něj.
function campaignPhoto(campaign) {
  const p = campaign.photo;
  return p ? { variant: 'a', img: MEDIA + p.desktop, imgMobile: MEDIA + p.mobile } : {};
}

// ---------------------------------------------------------------------------
// Kampaň: deštník nad akční nabídkou (campaign.js, plán kampaní 6. 10.)
// ---------------------------------------------------------------------------
// Kde v deštníku zákazník stojí: 'sale' = kategorie akční nabídky (stránka
// deštníku), 'clearance' = výprodej skladu, null = jinde. Stránkování
// (`/slevoakce/strana-2/`) patří ke stejné stránce.
function campaignPage(campaign) {
  // cesta stránky i kanonická URL (offline fixture leží na jiné cestě, kanonickou
  // nese z ostré; stránkování má obě s prefixem kategorie)
  const paths = [location.pathname];
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    try {
      paths.push(new URL(canonical.getAttribute('href'), location.href).pathname);
    } catch (e) {
      /* nečitelná kanonická URL — rozhoduje cesta stránky */
    }
  }
  const under = (href) =>
    !!href &&
    paths.some(function (p) {
      return p.indexOf(href) === 0;
    });
  if (under(campaign.href)) return 'sale';
  if (under(page('clearance'))) return 'clearance';
  return null;
}

// Dlaždice výprodeje skladu: fotka výprodejového zboží se zásobou (manifest
// `campaigns[].tile`), text kreslí šablona — bez textu zapečeného v obrázku,
// takže jde i na SK. Stejná dlaždice stojí v mřížce cizí kategorie i v pásu
// akční nabídky (`wide`).
function clearanceTileDef(campaign) {
  return {
    variant: 'product',
    after: 4,
    eyebrow: campaign.name,
    title: campaign.clearance,
    body: campaign.t.tileBody,
    cta: campaign.t.tileCta,
    href: page('clearance'),
    img: campaign.tile.img,
  };
}

// Nadpis a poslední drobeček kategorie akční nabídky nesou název kampaně
// (URL ani admin se nemění, SEO titulek stránky zůstává z adminu).
function renameSalePage(name) {
  const h1 = heading();
  if (h1 && h1.textContent.trim() !== name) h1.textContent = name;
  const crumbs = document.querySelectorAll('.breadcrumbs [itemprop="name"], .breadcrumbs .navigation-item-last');
  const last = crumbs[crumbs.length - 1];
  if (last && !last.querySelector('*') && last.textContent.trim() !== name) last.textContent = name;
}

// Pás pod hlavičkou: na akční nabídce dlaždice výprodeje skladu (shop bez
// výprodeje jen věta deštníku), na výprodeji odkaz zpět na celý deštník.
// Běží v každém re-initu (filtr, loadMore) a je idempotentní podle DOM.
function buildCampaignBand() {
  const campaign = activeCampaign();
  if (!campaign || !document.body.classList.contains('type-category')) return;
  const where = campaignPage(campaign);
  if (!where) return;
  if (where === 'sale') renameSalePage(campaign.name);
  // procházení výprodeje má výprodej skladu jako dlaždici v řadě kategorií
  if (listingSource()) return;
  const h1 = heading();
  const top = h1 && h1.closest('.category-top');
  if (!top || document.querySelector('.bs-camp-band')) return;

  const band = el('div', 'bs-camp-band bs-camp-band--' + where);
  band.dataset.bsCampaign = campaign.id;
  if (where === 'sale' && campaign.tile && campaign.clearance) {
    const tile = buildPromo(clearanceTileDef(campaign));
    tile.classList.add('bs-promo--wide');
    band.appendChild(tile);
  } else {
    const line = el('p', 'bs-camp-band__line');
    line.appendChild(icon('percent'));
    if (where === 'sale') {
      line.appendChild(el('span', null, campaign.t.bandText));
    } else {
      line.appendChild(el('span', null, campaign.t.clearanceBand + ' '));
      const back = el('a', 'bs-camp-band__link', campaign.t.clearanceBandLink);
      back.href = campaign.href;
      line.appendChild(back);
    }
    band.appendChild(line);
  }
  top.insertAdjacentElement('afterend', band);
  // na akční nabídce vede pás na výprodej skladu (shop bez výprodeje jen věta),
  // na výprodeji zpět na deštník
  trackPromo(band, campaign, 'listing_band', { target: band.querySelector('.bs-promo') ? 'clearance' : 'sale' });
}

function buildPromo(def) {
  const variant = def.variant === 'a' || def.variant === 'product' ? def.variant : 'b';
  const card = el('a', 'bs-promo bs-promo--' + variant + (def.tone ? ' bs-promo--' + def.tone : ''));
  card.href = def.href;

  if (variant === 'product') {
    // packshot na bílém poli, ne fotka přes celou kartu (ořez by ho ustřihl)
    const shot = el('span', 'bs-promo__shot');
    const img = document.createElement('img');
    img.src = def.img;
    img.alt = '';
    img.loading = 'lazy';
    shot.appendChild(img);
    card.appendChild(shot);
    const body = el('span', 'bs-promo__copy');
    if (def.eyebrow) body.appendChild(el('span', 'bs-promo__eyebrow', def.eyebrow));
    body.appendChild(el('span', 'bs-promo__title', def.title));
    if (def.body) body.appendChild(el('span', 'bs-promo__body', def.body));
    const cta = el('span', 'bs-promo__cta');
    cta.appendChild(el('span', null, def.cta));
    cta.appendChild(icon('arrow-right'));
    body.appendChild(cta);
    card.appendChild(body);
    return card;
  }

  if (def.img) {
    const img = document.createElement('img');
    img.className = 'bs-promo__img';
    img.src = def.img;
    img.alt = '';
    img.loading = 'lazy';
    if (def.imgMobile) {
      // dva ořezy přes <picture>; hranice = $bp-md. Obal má `display: contents`
      // (SCSS), jinak by absolutní fotka měřila inset vůči prázdnému inline boxu
      const pic = document.createElement('picture');
      const src = document.createElement('source');
      src.media = '(max-width: 768px)';
      src.srcset = def.imgMobile;
      pic.appendChild(src);
      pic.appendChild(img);
      card.appendChild(pic);
    } else {
      card.appendChild(img);
    }
    card.appendChild(el('span', 'bs-promo__overlay'));
  }
  if (def.eyebrow) card.appendChild(el('span', 'bs-promo__eyebrow', def.eyebrow));
  if (def.title) card.appendChild(el('span', 'bs-promo__title', def.title));
  if (def.body) card.appendChild(el('span', 'bs-promo__body', def.body));
  if (def.cta) {
    const cta = el('span', 'bs-promo__cta');
    cta.appendChild(el('span', null, def.cta));
    cta.appendChild(icon('arrow-right'));
    card.appendChild(cta);
  }
  return card;
}

// Počet sloupců mřížky se mění dle šířky (2/3/4/5) — čteme ho z gridu, ne z breakpointů.
function gridColumns(grid) {
  const tpl = getComputedStyle(grid).gridTemplateColumns || '';
  const n = tpl.split(' ').filter(function (v) { return v && v !== 'none'; }).length;
  return n > 0 ? n : 1;
}

// Střední pozice v řadě — karta nikdy nesmí být první ani poslední ve své řadě
// (na kraji mřížky vypadá jako uříznutá reklama, ne jako součást výpisu).
function promoSlot(row, cols) {
  return row * cols + Math.min(cols - 2, Math.floor(cols / 2));
}

// Promo karty se vkládají VŽDY, když má stránka aspoň 6 produktů (rozhodnutí
// Tomáše 2026-10-06). Dřív jen tehdy, když karta doplnila poslední řadu do
// plna: strana má 12 produktů a ve 2, 3 i 4 sloupcích je plná sama, takže se
// karta na plné straně neukázala nikdy a jinde podle náhody počtu. Na desktopu
// zabírá jednu buňku místo produktu, na mobilu celou řadu (SCSS `span 2`).
// Poslední řada pak může skončit kratší — to je vědomá cena za viditelnost.
function insertPromos() {
  if (listingSource()) return; // mřížku plní vlastní index, promo karta do ní nepatří
  const grid = document.querySelector('#products.products-page');
  if (!grid) return;
  const items = Array.prototype.slice.call(grid.children).filter(function (n) {
    return n.classList.contains('product');
  });
  if (items.length < 6) return;

  const cols = gridColumns(grid);
  if (cols < 2) return;

  const defs = promoDefs();
  const wanted = defs.length;
  // přebytečné karty z dřívějšího renderu (jiná šířka okna, loadMore) pryč
  Array.prototype.slice.call(grid.querySelectorAll('.bs-promo')).forEach(function (n, i) {
    if (i >= wanted) n.remove();
  });
  if (!wanted) return;

  for (let i = 0; i < wanted; i++) {
    const key = 'p' + i;
    if (grid.querySelector('.bs-promo[data-bs-promo-key="' + key + '"]')) continue;
    const card = buildPromo(defs[i]);
    card.setAttribute('data-bs-promo-key', key);
    if (defs[i].campaign) trackPromo(card, defs[i].campaign, 'listing_tile', { target: defs[i].target });
    // 2. a 5. řada (i u Black Friday, mock M16 a); s každou dřív vloženou kartou
    // se mřížka posune o buňku
    const slot = promoSlot(1 + i * 3, cols) - i;
    const anchor = grid.children[slot];
    if (anchor) grid.insertBefore(card, anchor);
    else grid.appendChild(card);
  }
}

// ---------------------------------------------------------------------------
// Podkategorie (README §Podkategorie) — karta `kolečko | název | chevron`
// ---------------------------------------------------------------------------
// Nativní markup je `ul.subcategories > li > a > span.image + span.text`; jen ho
// přeznačíme a doplníme chevron. Počet produktů návrh má, ale Shoptet ho v DOM
// nenese — řádek se prostě nevykreslí (datový nález patří do #41).
function buildSubcats() {
  const list = document.querySelector('ul.subcategories');
  if (!list || list.dataset.bsSubcats) return;
  list.dataset.bsSubcats = '1';
  list.classList.add('bs-subcats');

  Array.prototype.slice.call(list.children).forEach(function (li) {
    // prázdný oddělovač bootstrap gridu by v mřížce karet zabral buňku
    if (li.classList.contains('divider')) {
      li.remove();
      return;
    }
    const a = li.querySelector(':scope > a');
    if (!a || a.classList.contains('bs-subcat')) return;
    a.classList.add('bs-subcat');

    // parametrické kategorie (`li.parametric`) obrázek nemají → ikona v kolečku
    const image = a.querySelector('.image');
    if (image) image.classList.add('bs-subcat__ic');
    else {
      const box = el('span', 'bs-subcat__ic bs-subcat__ic--empty');
      box.appendChild(icon('grid'));
      a.insertAdjacentElement('afterbegin', box);
    }

    const text = a.querySelector('.text');
    if (text) {
      const wrap = el('span', 'bs-subcat__txt');
      text.insertAdjacentElement('beforebegin', wrap);
      text.classList.add('bs-subcat__name');
      wrap.appendChild(text);
    }

    a.appendChild(icon('chevron-down', 'bs-subcat__chev'));
  });
}

// ---------------------------------------------------------------------------
// Top produkty v kategorii (README §Top produkty) — restyle nativního bloku
// „Nejprodávanější". První položka je výrazná karta, další dvě řádky žebříčku.
// „Prodáno X ks za 30 dní" z návrhu je mock — datový zdroj neexistuje, takže
// se nekreslí (README §Interakce to označuje za podmíněné).
// ---------------------------------------------------------------------------
function catName() {
  const h1 = heading();
  return h1 ? (h1.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// „(–13 %)" z nativního `.price-save`, jinak dopočet z přeškrtnuté ceny
function discountText(card) {
  const save = (card.querySelector('.price-save') || {}).textContent || '';
  const m = save.match(/(\d+)\s*%/);
  if (m) return '−' + m[1] + ' %';

  const num = function (node) {
    const t = node ? (node.textContent || '').replace(/[\s\u00A0]/g, '') : '';
    const d = t.match(/(\d+(?:[.,]\d+)?)/);
    return d ? parseFloat(d[1].replace(',', '.')) : NaN;
  };
  const now = num(card.querySelector('.price-final strong') || card.querySelector('.price-final'));
  const was = num(card.querySelector('.price-standard'));
  const pct = discountPercent(was, now); // dolů jako Shoptet
  return pct === null ? '' : '−' + pct + ' %';
}

function buildTopProducts() {
  const wrap = document.querySelector('.products-top-wrapper');
  if (!wrap || wrap.dataset.bsTop) return;
  const list = wrap.querySelector('.products-top');
  const items = list ? Array.prototype.slice.call(list.querySelectorAll(':scope > .product')) : [];
  if (!items.length) return;
  wrap.dataset.bsTop = '1';
  wrap.classList.add('bs-top');

  // hlavička: medailonek + titulek + hairline doprava
  const head = el('div', 'bs-top__head');
  const mark = el('span', 'bs-top__mark');
  mark.appendChild(icon('rosette'));
  head.appendChild(mark);

  const h2 = wrap.querySelector('.products-top-header');
  const name = catName();
  const title = h2 || el('h2', null, L.topTitle(name));
  title.classList.add('bs-top__title');
  title.textContent = L.topTitle(name);
  head.appendChild(title);
  head.appendChild(el('span', 'bs-top__line'));
  wrap.insertAdjacentElement('afterbegin', head);

  if (list) list.classList.add('bs-top__grid');

  items.slice(0, 3).forEach(function (item, i) {
    const card = item.querySelector('.p');
    if (!card) return;
    item.classList.add('bs-top__item', i === 0 ? 'bs-top__item--hero' : 'bs-top__item--row');

    if (i === 0) {
      card.insertAdjacentElement('afterbegin', el('span', 'bs-top__flag', LABEL_TOP_FLAG));
      const disc = discountText(card);
      const prices = card.querySelector('.prices');
      if (disc && prices) prices.appendChild(el('span', 'bs-top__disc', disc));
    } else {
      card.insertAdjacentElement('afterbegin', el('span', 'bs-top__rank', String(i + 1)));
    }
  });
}

// ---------------------------------------------------------------------------

export function initCategoryPage() {
  if (!isListing()) return;
  if (!document.querySelector('#products')) return;

  // Pořadí je závazné: sloupce musí stát dřív, než se do pravého věší SEO blok;
  // SEO se vytahuje z perexu dřív, než hlavičku obalí `.bs-cat-intro__body`
  // (buildSeo počítá s přímými dětmi `.category-perex`).
  enhanceHeading();
  buildLayout();
  buildBottom();
  buildIntro();
  buildCampaignBand();
  buildSubcats();
  buildTopProducts();
  enhanceFilter();
  refreshChips();
  // ⚠ `insertPromos()` čte počet sloupců přes `getComputedStyle` — uprostřed boot
  // sekvence to vynucovalo layout nad 14 tis. uzly (866 ms self-time při CPU 4×,
  // profil 2026-09-11, F1). rAF běží až po zbytku zápisů, ale pořád PŘED paintem,
  // takže karty jsou na místě při prvním vykreslení a nic se neposune.
  requestAnimationFrame(insertPromos);
  buildEmptyStates();

  wireListingScroll();
  maybeScrollToProducts();

  document.documentElement.classList.add(READY);
}
